import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ContactActions from '../components/ContactActions';
import CtaBand from '../components/CtaBand';
import Icon from '../components/Icon';
import Layout, { PageHero } from '../components/Layout';
import { TROUBLE_AREAS } from '../constants/content';

export default function TroubleshootingPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const areaKey = TROUBLE_AREAS.some((a) => a.key === params.get('area')) ? params.get('area') : TROUBLE_AREAS[0].key;

  const update = (next) => {
    const merged = { area: areaKey, q: query, ...next };
    const clean = { area: merged.area };
    if (merged.q) clean.q = merged.q;
    setParams(clean, { replace: true });
  };

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      return TROUBLE_AREAS.flatMap((area) =>
        area.issues
          .filter((issue) => `${issue.symptom} ${issue.fixes.join(' ')}`.toLowerCase().includes(q))
          .map((issue) => ({ ...issue, area: area.label }))
      );
    }
    const area = TROUBLE_AREAS.find((a) => a.key === areaKey);
    return area.issues.map((issue) => ({ ...issue, area: area.label }));
  }, [areaKey, query]);

  return (
    <Layout>
      <PageHero
        eyebrow="Troubleshooting"
        title="Find the fix for your problem"
        lead="Choose an area or search for a symptom. Each issue lists the fixes we try first, in order."
        crumbs={[{ label: 'Troubleshooting' }]}
      />

      <section className="section">
        <div className="container trouble-grid">
          <aside className="trouble-nav">
            <label className="search-box">
              <span className="sr-only">Search problems</span>
              <input
                type="search"
                value={query}
                placeholder="Search — e.g. password, slow, merge"
                onChange={(e) => update({ q: e.target.value })}
              />
            </label>
            <ul role="tablist" aria-label="Problem areas">
              {TROUBLE_AREAS.map((area) => (
                <li key={area.key}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!query && area.key === areaKey}
                    className={`trouble-tab ${!query && area.key === areaKey ? 'is-active' : ''}`}
                    onClick={() => update({ area: area.key, q: '' })}
                  >
                    <Icon name={area.icon} size={20} />
                    <span>{area.label}</span>
                    <small>{area.issues.length}</small>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="trouble-results">
            {query ? (
              <p className="trouble-results__meta">
                {results.length} result{results.length === 1 ? '' : 's'} for “{query}”
              </p>
            ) : null}
            {results.length ? (
              results.map((issue, i) => (
                <details key={issue.symptom} className="issue" open={i === 0}>
                  <summary>
                    <span className="issue__tag">{issue.area}</span>
                    <span className="issue__title">{issue.symptom}</span>
                    <Icon name="chevron" size={20} />
                  </summary>
                  <ol className="steps">
                    {issue.fixes.map((fix) => (
                      <li key={fix}>{fix}</li>
                    ))}
                  </ol>
                </details>
              ))
            ) : (
              <div className="empty-card">
                <h2>No match yet</h2>
                <p>We haven’t written that one up — but a technician has almost certainly seen it. Describe it in live chat.</p>
                <ContactActions chatLabel="Ask a technician" />
              </div>
            )}

            <div className="trouble-help">
              <Icon name="chat" size={28} />
              <div>
                <h2>Tried everything above?</h2>
                <p>
                  Some problems need a second pair of eyes. Tell us what you see — we’ll work through it with you.
                  Or browse our <Link to="/guides">in-depth guides</Link>.
                </p>
              </div>
              <ContactActions chatLabel="Live Chat" />
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </Layout>
  );
}
